import React, { FC, useMemo, MouseEvent, useContext, useState } from "react";
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import {
  COLOR_PURPLE_0,
} from "./../../../app/ui/colorConstants";
import { Customer } from "../../../app/interfaces/customer";
import { ConvertNumberToCurrencyStr } from "../../../app/utils/numbers/convertNumberToCurrencyStr";
import CustomerContextMenu from "./contextMenu/CustomerContextMenu";
import { gridClasses } from "@mui/x-data-grid";
import { CustomerTableContext } from "../AdminCustomers";
import { useCustomerContextMenu } from "./contextMenu/useCustomerContextMenu";
import { usePersistentDataGrid } from "../../../app/services/hooks/usePersistentDataGrid";
import { OrganizationData } from "../../../app/interfaces/organizationData";
import { Tag } from "antd";
import { Button, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useSelector } from "react-redux";
import { authSelector } from "../../auth/authSlice";
import { useFirestore } from "react-redux-firebase";
import { customerQueries } from "../../../app/services/firestore/queries/customerQueries";
import notificationConfirm from "../../../app/system-components/toasters/notificationConfirm";
import notificationError from "../../../app/system-components/toasters/notificationError";
import { useFirestoreContext } from "../../../app/global-context/FirestoreContext";

// Helper function to format dates in a human-readable format
const formatDate = (value: any): string => {
  try {
    // Check if the value is a Firestore timestamp (has toDate method)
    if (value && typeof value.toDate === 'function') {
      value = value.toDate();
    }
    
    // Format the date if it's a valid Date object
    if (value instanceof Date) {
      const options: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      return new Intl.DateTimeFormat('en-US', options).format(value);
    }
    
    // If not a valid date, return string representation
    return String(value || '');
  } catch (error) {
    return String(value || '');
  }
};

// Helper function to get user name from ID
const getUserName = (userId: string, users: any[]): string => {
  if (!userId || !users || users.length === 0) return '';
  
  const user = users.find(u => u.id === userId);
  if (!user) return '';
  
  return `${user.firstName || ''} ${user.lastName || ''}`.trim();
};

// Helper function to convert customers to CSV with all properties
const customersToCSV = (customers: Customer[], users: any[]): string => {
  if (!customers || customers.length === 0) {
    return '';
  }

  // Define properties to exclude
  const excludedProps = ['justifi','stripeAccount'];

  // Define friendly column name mappings
  const friendlyColumnNames: Record<string, string> = {
    // Customer info
    customerDisplayName: 'Customer Name',
    customerCompanyName: 'Company',
    firstName: 'First Name',
    lastName: 'Last Name',
    middleName: 'Middle Name',
    suffix: 'Suffix',
    title: 'Title',
    
    // Contact info
    email: 'Email Address',
    phone: 'Phone Number',
    mobile: 'Mobile Number',
    faxNo: 'Fax Number',
    otherContact: 'Other Contact',
    
    // Addresses
    customerBillingAddress: 'Billing Address',
    customerBillingCity: 'Billing City',
    customerBillingState: 'Billing State',
    customerBillingZip: 'Billing ZIP',
    customerShippingAddress: 'Shipping Address',
    customerShippingCity: 'Shipping City',
    customerShippingState: 'Shipping State', 
    customerShippingZip: 'Shipping ZIP',
    customerFullBillingAddressStr: 'Full Billing Address',
    customerFullShippingAddressStr: 'Full Shipping Address',
    customerShipToBilling: 'Ship to Billing Address',
    
    // Financial
    balance: 'Balance',
    credit: 'Credit',
    customerTaxRate: 'Tax Rate',
    customerTaxRateDetails: 'Tax Rate Details',
    customerTaxExempt: 'Tax Exempt',
    defaultDiscountPercent: 'Default Discount %',
    damageWaiverExempt: 'Damage Waiver Exempt',
    damageWaiverExemptReason: 'Damage Waiver Exempt Reason',
    
    // Metadata
    id: 'Customer ID',
    createdBy: 'Created By',
    createdOn: 'Created Date',
    updatedBy: 'Last Updated By',
    updatedOn: 'Last Updated Date',
    isActive: 'Active Status',
    notes: 'Notes',
    checkNotesReminder: 'Check Notes Reminder',
    tagsQueryHook: 'Tags',
    
    // Integration
    qbId: 'QuickBooks ID',
    orgId: 'Organization ID',
    stripeAccount: 'Stripe Account',
    
    // Other
    files: 'Files',
    imageUrl: 'Profile Image',
    additionalImages: 'Additional Images',
    additionalContacts: 'Additional Contacts',

    customerBillingAddressStr: 'Billing Address',
    customerShippingAddressStr: 'Shipping Address',
    isTaxExempt: 'Tax Exempt',
    shipToBilling: 'Ship to Billing',
    
    
  };

  // Get friendly name for a key
  const getFriendlyColumnName = (key: string): string => {
    return friendlyColumnNames[key] || key;
  };

  // Get all possible keys from the first customer
  const firstCustomer = customers[0];
  let allKeys = Object.keys(firstCustomer).filter(key => !excludedProps.includes(key));
  
  // Scan through all customers to make sure we include all possible keys
  customers.forEach(customer => {
    const customerKeys = Object.keys(customer);
    customerKeys.forEach(key => {
      if (!allKeys.includes(key) && !excludedProps.includes(key)) {
        allKeys.push(key);
      }
    });
  });

  // Create header row with user-friendly column names
  const headers = allKeys.map(key => `"${getFriendlyColumnName(key)}"`).join(',');
  
  // Create data rows with all properties
  const rows = customers.map(customer => {
    return allKeys.map(key => {
      const value = customer[key as keyof Customer];
      
      // Handle user fields (createdBy, updatedBy)
      if (key === 'createdBy' || key === 'updatedBy') {
        const userName = getUserName(value as string, users);
        return `"${userName}"`;
      }
      
      // Handle special cases
      if (key === 'balance' || key === 'credit') {
        return `"${ConvertNumberToCurrencyStr(value as number, "$")}"`;
      }
      
      // Handle date fields
      if (key === 'createdOn' || key === 'updatedOn' || key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) {
        return `"${formatDate(value)}"`;
      }
      
      // Handle special cases for nested objects or arrays
      if (typeof value === 'object' && value !== null) {
        if (key === 'qbId') {
          // Special case for QuickBooks field
          return `"${customer.qbId ? "Synced" : "Error"}"`;
        } else if (value instanceof Date) {
          return `"${formatDate(value)}"`;
        } else if (Array.isArray(value)) {
          return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
        } else {
          // Check if it might be a date object (Firestore timestamp)
          if (typeof value.toDate === 'function') {
            return `"${formatDate(value)}"`;
          }
          return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
        }
      }
      
      // Escape quotes and wrap in quotes
      return `"${String(value || '').replace(/"/g, '""')}"`;
    }).join(',');
  }).join('\n');
  
  return `${headers}\n${rows}`;
};

// Helper function to download CSV
const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const CustomerDataGrid: FC<{
  orgData: OrganizationData;
  data: Customer[];
  loading: boolean;
  handleEdit: (c: Customer) => void;
  onDeleteComplete: () => void;
}> = ({ orgData, data, loading, handleEdit, onDeleteComplete }) => {

  const { actions } = useContext(CustomerTableContext);
  const { users } = useFirestoreContext();
  const [isExporting, setIsExporting] = useState(false);
  const { fsOrgPrefix } = useSelector(authSelector);
  const firestore = useFirestore();

  const persistentDataGrid = usePersistentDataGrid(
    "ADMIN_CUSTOMER_TABLE_STATE"
  );

  const customerContextMenuControl = useCustomerContextMenu({
    handleEdit,
    onDeleteComplete,
  });

  const columnsMUI = useMemo(getColumns, [data, orgData]);

  const toggleContextMenu = (
    event: React.MouseEvent<HTMLElement, globalThis.MouseEvent>,
    customer: Customer
  ) => {
    if (!customerContextMenuControl.contextConfig.selectedCustomer)
      customerContextMenuControl.setContextMenuConfig({
        x: event.clientX - 2,
        y: event.clientY - 4,
        visible: true,
        selectedCustomer: customer,
      });
    else customerContextMenuControl.resetContext();
  };

  const handleDoubleClick = (
    event: MouseEvent<HTMLElement>,
    record: Customer
  ) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    toggleContextMenu(event, record);
  };

  const handleRightClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const rowId = event.currentTarget.getAttribute("data-id");
    const customer = data.find((c) => c.id === rowId);
    if (customer) toggleContextMenu(event, customer);
  };

  // Custom export handler that fetches all customers
  const handleExportAll = async () => {
    try {
      setIsExporting(true);
      
      // Fetch all customers directly using customerQueries
      let allCustomers = data;
      
      if (fsOrgPrefix) {
        const customerService = customerQueries({ firestore }, fsOrgPrefix);
        // Guard against service being undefined
        if (customerService && typeof customerService.fetchCustomers === 'function') {
          const fetchedCustomers = await customerService.fetchCustomers();
          if (fetchedCustomers && fetchedCustomers.length > 0) {
            allCustomers = fetchedCustomers;
          }
        }
      }
      
      // Convert to CSV
      const csvContent = customersToCSV(allCustomers, users);
      
      // Download file
      downloadCSV(csvContent, `customers-export-${new Date().toISOString().split('T')[0]}.csv`);
      notificationConfirm(
        "Export Complete", 
        `Successfully exported ${allCustomers.length} customer${allCustomers.length !== 1 ? 's' : ''}`
      );
    } catch (error) {
      console.error('Error exporting customers:', error);
      notificationError("Export Failed", "There was an error exporting customers. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton style={{ color: COLOR_PURPLE_0 }} />
        <GridToolbarDensitySelector style={{ color: COLOR_PURPLE_0 }} />
        <Button
          startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
          style={{ color: COLOR_PURPLE_0 }}
          disabled={isExporting}
          onClick={handleExportAll}
        >
          {isExporting ? 'Exporting...' : 'Export All'}
        </Button>
        <GridToolbarFilterButton style={{ color: COLOR_PURPLE_0 }} />
      </GridToolbarContainer>
    );
  };

  const handleOnPaginationChange = (props: GridPaginationModel) => {
    const lastIndexOnPage = (props.page + 1) * props.pageSize;
    const lastIndexOnNextPage = (props.page + 2) * props.pageSize;
    const shouldLoadMore =
      data.length >= lastIndexOnPage && data.length <= lastIndexOnNextPage;
    if (shouldLoadMore) actions.loadNext();
  };

  return (
    <div
      style={{
        width: "100%",
        paddingLeft: "8px",
        paddingRight: "8px",
        backgroundColor: "white",
      }}
    >
      <DataGridPro<Customer>
        style={{ paddingLeft: "8px" }}
        autoHeight={true}
        apiRef={persistentDataGrid.apiRef}
        loading={loading}
        columns={columnsMUI}
        rows={data}
        pagination={true}
        onPaginationModelChange={handleOnPaginationChange}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          sorting: {
            sortModel: [{ field: "customerDisplayName", sort: "asc" }],
          },
        }}
        // pageSizeOptions={[25, 50, 100]}
        sx={{
          [`& .${gridClasses.cell}`]: {
            py: 1,
          },
        }}
        slots={{
          toolbar: CustomToolbar,
        }}
        onRowDoubleClick={(params, event) =>
          handleDoubleClick(event, params.row)
        }
        slotProps={{
          row: {
            onContextMenu: handleRightClick,
            style: { cursor: "context-menu", minHeight: "56px" },
          },
        }}
        onColumnOrderChange={persistentDataGrid.storeGridState}
        onColumnWidthChange={persistentDataGrid.storeGridState}
        onColumnVisibilityModelChange={persistentDataGrid.storeGridState}
        onPinnedColumnsChange={persistentDataGrid.storeGridState}
      />
      <CustomerContextMenu control={customerContextMenuControl} />
    </div>
  );

  function getColumns() {
    const cols: GridColDef[] = [
      {
        headerName: "Customer",
        field: "customerDisplayName",
        width: 200,
        renderCell: (params) => (
          <div
            onClick={(e) => {
              e.preventDefault();
              const customer = data.find((c) => c.id === params.id);
              if (customer) handleEdit(customer);
            }}
            style={{
              cursor: "pointer",
            }}
          >
            {params.value}
          </div>
        ),
      },
      {
        headerName: "Company",
        field: "customerCompanyName",
        width: 200
      },
      {
        headerName: "Address",
        field: "customerShippingAddressStr",
        width: 250,
      },
      { headerName: "Email", field: "email", width: 250 },
      { headerName: "Phone", field: "phone", width: 150 },
      { headerName: "Mobile", field: "mobile", width: 150 },
      {
        headerName: "Balance",
        field: "balance",
        width: 100,
        valueFormatter: (params) =>
          ConvertNumberToCurrencyStr(params.value, "$"),
      },
      {
        headerName: "Credit",
        field: "credit",
        width: 100,
        valueFormatter: (params) =>
          ConvertNumberToCurrencyStr(params.value, "$"),
      },
    ];

    if (orgData && orgData.qboRealmId && orgData.qboRealmId !== "") {
      cols.push({
        headerName: "QuickBooks",
        field: "qbId",
        width: 100,
        renderCell: (params) =>
          params.row.qbId ? (
            <div>
              <Tag color={"success"}>Synced</Tag>
            </div>
          ) : (
            <div>
              <Tag color={"error"}>Error</Tag>
            </div>
          ),
      });
    }
    return cols;
  }
};

export default CustomerDataGrid;
